import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
  Button,
} from '@noor/ui';
import { cn } from '@noor/lib/cn';

export interface RoleOption {
  value: string;
  label: string;
}

interface MobileRolePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  options: RoleOption[];
  /** The role selected when the sheet opens. */
  value: string;
  /** Commits the pick — fired by «Готово», not per tap. */
  onConfirm: (value: string) => void;
  backLabel: string;
  confirmLabel: string;
}

/**
 * The role picker the mobile employee form opens (Figma node 26295:98832).
 *
 * A BOTTOM SHEET over a dimmed backdrop, not `FormField.Select`: below the
 * shared select's breakpoint it falls back to an anchored popover, where the
 * design slides a rounded card up from the bottom carrying a title, divided
 * radio rows, and a «Назад» / «Готово» pair.
 *
 * Selection is LOCAL until confirmed — the design's «Назад» must leave the
 * field as it was, and only «Готово» commits. The sheet is mounted only while
 * open (callers render it conditionally), so each open seeds from what the
 * field actually holds with a plain `useState` and needs no re-seeding effect
 * — the same construction `MobileOptionPicker` uses, and the reason neither
 * trips the `react-hooks/set-state-in-effect` rule.
 *
 * The rows are real radios (`role="radiogroup"` + `role="radio"`): exactly one
 * role is ever selected, and the design draws circles, so the semantics and
 * the glyph agree here — unlike the review filter sheet, which needed square
 * boxes over radio semantics.
 */
export function MobileRolePicker({
  open,
  onOpenChange,
  title,
  options,
  value,
  onConfirm,
  backLabel,
  confirmLabel,
}: MobileRolePickerProps) {
  const [picked, setPicked] = useState(value);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-[2rem] bg-card',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom',
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogDescription className="sr-only">{title}</DialogDescription>

          <div className="flex flex-col gap-6 px-4 pt-4 pb-2">
            <DialogTitle className="text-[1.375rem] font-semibold tracking-[-0.04em] text-foreground">
              {title}
            </DialogTitle>

            <div role="radiogroup" aria-label={title} className="flex flex-col">
              {options.map((option, index) => (
                <div key={option.value}>
                  {index > 0 && <div className="h-px w-full rounded-sm bg-border" />}
                  <button
                    type="button"
                    role="radio"
                    aria-checked={picked === option.value}
                    onClick={() => setPicked(option.value)}
                    className="flex w-full cursor-pointer items-center gap-2 py-4 text-left outline-none focus-visible:rounded-lg focus-visible:ring-[3px] focus-visible:ring-ring/40"
                  >
                    <span className="min-w-0 flex-1 truncate text-base font-normal tracking-tight text-foreground">
                      {option.label}
                    </span>
                    <RadioGlyph checked={picked === option.value} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pb-[env(safe-area-inset-bottom)]">
              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={() => onOpenChange(false)}
                className="h-14 flex-1 rounded-full text-base font-semibold"
              >
                {backLabel}
              </Button>
              <Button
                type="button"
                size="lg"
                onClick={() => {
                  onConfirm(picked);
                  onOpenChange(false);
                }}
                className="h-14 flex-1 rounded-full text-base font-semibold"
              >
                {confirmLabel}
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

/**
 * The 24px radio mark (Figma 26295:98880 checked / 26295:98885 unchecked):
 * a filled brand ring with a white centre when picked, a plain grey outline
 * when not. Drawn here rather than reused from `@noor/ui`'s `RadioGroup`,
 * which renders a smaller mark inside its own row layout.
 */
function RadioGlyph({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
        checked ? 'border-primary bg-primary' : 'border-control-border',
      )}
    >
      {checked && <span className="size-2 rounded-full bg-primary-foreground" />}
    </span>
  );
}
